/**
 * Native shell wiring — the small set of things that separate "a WebView with a
 * website in it" from something that reads as an app.
 *
 * Every call is a no-op on the website: the plugins have web fallbacks, but we
 * still gate on `isNativeApp` so the browser build does no work and registers no
 * listeners. Imports are dynamic so the plugin code lands in a lazily-loaded
 * chunk the website never fetches.
 *
 * Deliberately NOT here: anything that needs a rebuild to change. This file only
 * talks to the shell; all content and behaviour still comes from the web bundle.
 */
import { isNativeApp } from './platform';

/** Must match `--color-vellum` in both themes (client/src/index.css). */
const BAR_LIGHT = '#faf7f0';
const BAR_DARK = '#171226';

/**
 * Paint the status bar to match the current theme.
 *
 * Called again by PreferencesProvider whenever the resolved theme flips, so the
 * bar never sits dark over a light page. `setStyle` picks the icon colour:
 * `Style.Dark` means dark CONTENT on a light bar, which is the opposite of what
 * the name suggests — hence the mapping being spelled out here.
 */
export async function applyStatusBarTheme(theme: 'light' | 'dark'): Promise<void> {
  if (!isNativeApp) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: theme === 'dark' ? Style.Light : Style.Dark });
    // Android only; throws (harmlessly) on iOS, where the bar is transparent.
    await StatusBar.setBackgroundColor({ color: theme === 'dark' ? BAR_DARK : BAR_LIGHT }).catch(
      () => undefined,
    );
  } catch {
    /* plugin unavailable — the app still works, the bar is just default */
  }
}

/**
 * One-time shell setup, awaited before the first render.
 *
 * - Android hardware back button drives the router's history rather than
 *   killing the app; at the root of the stack it backgrounds the app the way a
 *   native app does (`minimizeApp`, not `exitApp` — exiting loses your place).
 * - The keyboard resizes the page instead of sliding the whole WebView up,
 *   which would push the header off-screen while typing in search.
 * - The splash screen is dismissed by us, not on a timer, so the handoff
 *   happens on the first painted frame instead of flashing white.
 */
export async function initNativeShell(): Promise<void> {
  if (!isNativeApp) return;

  try {
    const { App } = await import('@capacitor/app');
    await App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        void App.minimizeApp();
      }
    });
  } catch {
    /* no App plugin — Android back button falls back to default behaviour */
  }

  try {
    const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    await Keyboard.setScroll({ isDisabled: false });
  } catch {
    /* iOS simulator / web — nothing to configure */
  }
}

/**
 * Hide the splash screen. Called from a `requestAnimationFrame` after mount, so
 * the first real frame is already on the GPU when the splash goes away.
 */
export async function hideSplash(): Promise<void> {
  if (!isNativeApp) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch {
    /* nothing to hide */
  }
}

/**
 * Open an external URL in the system browser rather than inside the app.
 *
 * A remote page loaded into the app's own WebView would replace the bundle and
 * strand the user outside the app with no way back — and on iOS it is grounds
 * for review rejection. Returns false on the web so callers can fall through to
 * a normal anchor.
 */
export async function openExternal(url: string): Promise<boolean> {
  if (!isNativeApp) return false;
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
    return true;
  } catch {
    return false;
  }
}
